import { csvEscape, formatMoneyCents } from './format';
import { formatPeriodLabel } from './periods';
import { formatZonedDate, formatZonedDateTime } from './timezone';
import { formatCashierBucketLabel } from './cashierEngine';
import type { CashierPaymentRow, CashierReportResult } from './cashierTypes';
import { isKnownPaymentMethod, PAYMENT_METHOD_LABEL_KEYS } from './paymentMethods';
import {
  CARD_TINTS,
  COLORS,
  CONTENT_WIDTH,
  MARGIN,
  PdfDocument,
  drawReportHeader,
  measureText,
  pdfSafeText,
} from './pdfKit';

export interface CashierExportLabels {
  title: string;
  subtitle: string;
  period: string;
  generated: string;
  clinic: string;
  brand: string;
  keyFigures: string;
  totalReceived: string;
  refunds: string;
  netReceived: string;
  outstanding: string;
  outstandingHint: string;
  outstandingInvoices: string;
  outstandingEmpty: string;
  outstandingTotal: string;
  issued: string;
  due: string;
  invoiceTotal: string;
  invoicePaid: string;
  invoiceStatusLabels: Record<string, string>;
  transactions: string;
  paymentMethods: string;
  method: string;
  amount: string;
  count: string;
  total: string;
  dailySummary: string;
  date: string;
  received: string;
  net: string;
  refundSection: string;
  refundCount: string;
  cashReceived: string;
  cashTransactions: string;
  clinioPay: string;
  feesUnavailable: string;
  payoutUnavailable: string;
  transactionDetail: string;
  dateTime: string;
  patient: string;
  invoice: string;
  channel: string;
  reference: string;
  status: string;
  practitioner: string;
  clinicColumn: string;
  channelClinioPay: string;
  channelOther: string;
  statusReceived: string;
  statusRefunded: string;
  truncated: string;
  footerReport: string;
  page: string;
  pageOf: string;
  methodLabels: Record<string, string>;
}

function methodLabel(key: string, labels: CashierExportLabels): string {
  if (labels.methodLabels[key]) return labels.methodLabels[key];
  if (isKnownPaymentMethod(key)) return labels.methodLabels[PAYMENT_METHOD_LABEL_KEYS[key]] ?? key;
  return key.replace(/_/g, ' ').replace(/^\w/, (c) => c.toUpperCase());
}

function money(cents: number, currency: string, locale: string): string {
  return formatMoneyCents(cents, currency, locale);
}

function channelLabel(channel: string, labels: CashierExportLabels): string {
  return channel === 'clinio_pay' ? labels.channelClinioPay : labels.channelOther;
}

function statusLabel(status: string, labels: CashierExportLabels): string {
  return status === 'refunded' ? labels.statusRefunded : labels.statusReceived;
}

function invoiceStatusLabel(status: string, labels: CashierExportLabels): string {
  return labels.invoiceStatusLabels[status] ?? status;
}

function transactionNet(row: CashierPaymentRow): number {
  return row.kind === 'refund' ? -Math.abs(row.amount_cents) : row.amount_cents;
}

function transactionGross(row: CashierPaymentRow): number {
  return row.kind === 'refund' ? 0 : row.amount_cents;
}

function transactionRefund(row: CashierPaymentRow): number {
  return row.kind === 'refund' ? Math.abs(row.amount_cents) : 0;
}

export function cashierReportToCsv(
  result: CashierReportResult,
  labels: CashierExportLabels,
  locale = 'da-DK',
): string {
  const lines: string[] = [];
  const push = (values: string[]) => lines.push(values.map(csvEscape).join(','));
  const currency = result.currency;

  push([labels.title]);
  push([labels.period, formatPeriodLabel(result.period, locale)]);
  if (result.clinic_name) push([labels.clinic, result.clinic_name]);
  push([]);
  push([labels.keyFigures]);
  push([labels.totalReceived, money(result.summary.total_received_cents, currency, locale)]);
  push([labels.refunds, money(result.summary.refunds_cents, currency, locale)]);
  push([labels.netReceived, money(result.summary.net_received_cents, currency, locale)]);
  push([labels.outstanding, money(result.summary.outstanding_cents, currency, locale)]);
  push([labels.transactions, String(result.summary.transaction_count)]);
  push([]);
  push([labels.paymentMethods, labels.received, labels.refunds, labels.net, labels.transactions]);
  for (const method of result.methods) {
    push([
      methodLabel(method.method_key, labels),
      money(method.received_cents, currency, locale),
      money(method.refund_cents, currency, locale),
      money(method.net_cents, currency, locale),
      String(method.transaction_count),
    ]);
  }
  push([
    labels.total,
    money(result.summary.total_received_cents, currency, locale),
    money(result.summary.refunds_cents, currency, locale),
    money(result.summary.net_received_cents, currency, locale),
    String(result.summary.transaction_count),
  ]);
  push([]);
  push([labels.outstandingInvoices]);
  if (result.outstandingInvoices.length === 0) {
    push([labels.outstandingEmpty]);
  } else {
    push([
      labels.invoice,
      labels.patient,
      labels.issued,
      labels.due,
      labels.invoiceTotal,
      labels.invoicePaid,
      labels.outstanding,
      labels.status,
    ]);
    for (const invoice of result.outstandingInvoices) {
      push([
        invoice.invoice_number ?? '',
        invoice.patient_name ?? '',
        formatZonedDate(invoice.issued_at, locale, result.timezone),
        formatZonedDate(invoice.due_at, locale, result.timezone),
        money(invoice.total_cents, currency, locale),
        money(invoice.paid_cents, currency, locale),
        money(invoice.outstanding_cents, currency, locale),
        invoiceStatusLabel(invoice.status, labels),
      ]);
    }
    push([
      labels.outstandingTotal,
      '',
      '',
      '',
      '',
      '',
      money(result.summary.outstanding_cents, currency, locale),
      '',
    ]);
  }
  push([]);
  push([
    labels.dateTime,
    labels.patient,
    labels.invoice,
    labels.reference,
    labels.method,
    labels.channel,
    labels.received,
    labels.refunds,
    labels.net,
    labels.status,
    labels.practitioner,
    labels.clinicColumn,
  ]);
  for (const row of result.transactions) {
    push([
      formatZonedDateTime(row.payment_date, locale, result.timezone),
      row.patient_name ?? '',
      row.invoice_number ?? '',
      row.payment_reference ?? '',
      methodLabel(row.payment_method_key, labels),
      channelLabel(row.channel, labels),
      money(transactionGross(row), currency, locale),
      money(transactionRefund(row), currency, locale),
      money(transactionNet(row), currency, locale),
      statusLabel(row.status, labels),
      row.practitioner_name ?? '',
      row.clinic_name ?? '',
    ]);
  }
  return `\uFEFF${lines.join('\n')}`;
}

function xmlEscape(value: string): string {
  return value
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

function cell(value: string): string {
  return `<Cell><Data ss:Type="String">${xmlEscape(value)}</Data></Cell>`;
}

function rowXml(values: string[]): string {
  return `<Row>${values.map(cell).join('')}</Row>`;
}

export function cashierReportToExcelXml(
  result: CashierReportResult,
  labels: CashierExportLabels,
  locale = 'da-DK',
): string {
  const csv = cashierReportToCsv(result, labels, locale).replace(/^\uFEFF/, '');
  const rows = csv.split('\n').map((line) => {
    const values: string[] = [];
    let current = '';
    let quoted = false;
    for (let i = 0; i < line.length; i += 1) {
      const ch = line[i];
      if (quoted) {
        if (ch === '"' && line[i + 1] === '"') {
          current += '"';
          i += 1;
        } else if (ch === '"') quoted = false;
        else current += ch;
      } else if (ch === '"') quoted = true;
      else if (ch === ',') {
        values.push(current);
        current = '';
      } else current += ch;
    }
    values.push(current);
    return rowXml(values);
  });
  return `<?xml version="1.0"?>
<Workbook xmlns="urn:schemas-microsoft-com:office:spreadsheet"
 xmlns:ss="urn:schemas-microsoft-com:office:spreadsheet">
<Worksheet ss:Name="${xmlEscape(labels.title.slice(0, 31))}"><Table>${rows.join('')}</Table></Worksheet>
</Workbook>`;
}

function fitValueSize(value: string, maxWidth: number, preferred: number): number {
  let size = preferred;
  while (size > 8 && measureText(value, size, true) > maxWidth) size -= 0.5;
  return size;
}

function drawSectionTitle(doc: PdfDocument, title: string) {
  doc.ensureSpace(28);
  doc.text(title.toUpperCase(), MARGIN, doc.y - 1, {
    size: 8,
    bold: true,
    color: COLORS.lavenderAccent,
  });
  doc.gap(5);
  doc.line(MARGIN, doc.y, MARGIN + 22, doc.y, COLORS.accent, 1.1);
  doc.gap(11);
}

function drawTable(
  doc: PdfDocument,
  headers: string[],
  rows: string[][],
  widths: number[],
) {
  const rowH = 16;
  const headerH = 18;
  doc.ensureSpace(headerH + rowH);
  const headerBottom = doc.y - headerH;
  doc.fillRoundedRect(MARGIN, headerBottom, CONTENT_WIDTH, headerH, 4, COLORS.headerBg);
  let x = MARGIN + 8;
  headers.forEach((header, index) => {
    doc.text(header, x, doc.y - 12, {
      size: 7,
      bold: true,
      color: COLORS.muted,
      maxWidth: widths[index],
      align: index === 0 ? 'left' : 'right',
    });
    x += widths[index];
  });
  doc.y -= headerH;
  rows.forEach((cells, rowIndex) => {
    doc.ensureSpace(rowH);
    if (rowIndex % 2 === 1) {
      doc.fillRect(MARGIN, doc.y - rowH + 2, CONTENT_WIDTH, rowH, COLORS.altRow);
    }
    let cellX = MARGIN + 8;
    cells.forEach((value, index) => {
      doc.text(pdfSafeText(value), cellX, doc.y - 11, {
        size: 8,
        bold: rowIndex === rows.length - 1,
        maxWidth: widths[index] - 4,
        align: index === 0 ? 'left' : 'right',
      });
      cellX += widths[index];
    });
    doc.y -= rowH;
  });
  doc.gap(12);
}

export function cashierReportToPdf(
  result: CashierReportResult,
  labels: CashierExportLabels,
  options: { includeDetails?: boolean; generatedAt?: Date; locale?: string } = {},
): Uint8Array {
  const locale = options.locale ?? 'da-DK';
  const generatedAt = options.generatedAt ?? new Date();
  const currency = result.currency;
  const period = formatPeriodLabel(result.period, locale);
  const doc = new PdfDocument();
  doc.footerLeft = `${labels.brand} · ${labels.footerReport} · ${period}`;
  doc.footerRightPrefix = labels.page;
  doc.footerOf = labels.pageOf;

  const meta = [
    ...(result.clinic_name ? [{ label: labels.clinic, value: result.clinic_name }] : []),
    { label: labels.period, value: period },
    {
      label: labels.generated,
      value: new Intl.DateTimeFormat(locale, { day: 'numeric', month: 'long', year: 'numeric' }).format(generatedAt),
    },
  ];
  drawReportHeader(doc, {
    brand: labels.brand,
    title: labels.title,
    subtitle: labels.subtitle,
    meta,
  });

  drawSectionTitle(doc, labels.keyFigures);
  const figures = [
    { label: labels.totalReceived, value: money(result.summary.total_received_cents, currency, locale) },
    { label: labels.refunds, value: money(result.summary.refunds_cents, currency, locale) },
    { label: labels.netReceived, value: money(result.summary.net_received_cents, currency, locale) },
    { label: labels.outstanding, value: money(result.summary.outstanding_cents, currency, locale) },
    { label: labels.transactions, value: String(result.summary.transaction_count) },
  ];
  const gap = 7;
  const figureRows = [figures.slice(0, 3), figures.slice(3)];
  figureRows.forEach((row) => {
    const cardW = (CONTENT_WIDTH - gap * (row.length - 1)) / row.length;
    const cardH = 48;
    doc.ensureSpace(cardH + 6);
    row.forEach((figure, index) => {
      const x = MARGIN + index * (cardW + gap);
      const tint = CARD_TINTS[index % CARD_TINTS.length];
      doc.fillRoundedRect(x, doc.y - cardH, cardW, cardH, 7, tint.bg);
      doc.strokeRoundedRect(x, doc.y - cardH, cardW, cardH, 7, COLORS.line, 0.45);
      doc.fillRoundedRect(x + 8, doc.y - cardH + 10, 2.2, cardH - 20, 1, tint.accent);
      doc.text(figure.label, x + 14, doc.y - 13, { size: 7, color: COLORS.muted, maxWidth: cardW - 20 });
      const valueSize = fitValueSize(figure.value, cardW - 20, 12);
      doc.text(figure.value, x + 14, doc.y - 32, { size: valueSize, bold: true, maxWidth: cardW - 20 });
    });
    doc.y -= cardH;
    doc.gap(8);
  });
  doc.gap(6);

  if (result.methods.length > 0) {
    drawSectionTitle(doc, labels.paymentMethods);
    const widths = [160, 95, 90, 90, 88];
    drawTable(
      doc,
      [labels.method, labels.received, labels.refunds, labels.net, labels.transactions],
      [
        ...result.methods.map((method) => [
          methodLabel(method.method_key, labels),
          money(method.received_cents, currency, locale),
          money(method.refund_cents, currency, locale),
          money(method.net_cents, currency, locale),
          String(method.transaction_count),
        ]),
        [
          labels.total,
          money(result.summary.total_received_cents, currency, locale),
          money(result.summary.refunds_cents, currency, locale),
          money(result.summary.net_received_cents, currency, locale),
          String(result.summary.transaction_count),
        ],
      ],
      widths,
    );
  }

  drawSectionTitle(doc, labels.outstandingInvoices);
  if (result.outstandingInvoices.length === 0) {
    doc.text(labels.outstandingEmpty, MARGIN, doc.y - 2, { size: 8, color: COLORS.muted, maxWidth: CONTENT_WIDTH });
    doc.gap(16);
  } else {
    drawTable(
      doc,
      [
        labels.invoice,
        labels.patient,
        labels.issued,
        labels.due,
        labels.invoiceTotal,
        labels.invoicePaid,
        labels.outstanding,
      ],
      [
        ...result.outstandingInvoices.map((invoice) => [
          invoice.invoice_number ?? '—',
          invoice.patient_name ?? '—',
          formatZonedDate(invoice.issued_at, locale, result.timezone),
          formatZonedDate(invoice.due_at, locale, result.timezone),
          money(invoice.total_cents, currency, locale),
          money(invoice.paid_cents, currency, locale),
          money(invoice.outstanding_cents, currency, locale),
        ]),
        [
          labels.outstandingTotal,
          '',
          '',
          '',
          '',
          '',
          money(result.summary.outstanding_cents, currency, locale),
        ],
      ],
      [78, 90, 70, 70, 75, 70, 70],
    );
  }

  if (result.periodRows.length > 0) {
    drawSectionTitle(doc, labels.dailySummary);
    drawTable(
      doc,
      [labels.date, labels.received, labels.refunds, labels.net, labels.transactions],
      [
        ...result.periodRows.map((row) => [
          formatCashierBucketLabel(row.bucket, result.grain, locale),
          money(row.received_cents, currency, locale),
          money(row.refund_cents, currency, locale),
          money(row.net_cents, currency, locale),
          String(row.transaction_count),
        ]),
        [
          labels.total,
          money(result.summary.total_received_cents, currency, locale),
          money(result.summary.refunds_cents, currency, locale),
          money(result.summary.net_received_cents, currency, locale),
          String(result.summary.transaction_count),
        ],
      ],
      [140, 110, 100, 100, 73],
    );
  }

  if (result.refunds.length > 0) {
    drawSectionTitle(doc, labels.refundSection);
    drawTable(
      doc,
      [labels.method, labels.refunds, labels.refundCount],
      [
        ...result.refunds.map((method) => [
          methodLabel(method.method_key, labels),
          money(method.refund_cents, currency, locale),
          String(method.refund_count),
        ]),
        [labels.total, money(result.summary.refunds_cents, currency, locale), String(result.summary.refund_count)],
      ],
      [250, 160, 113],
    );
  }

  const includeDetails =
    options.includeDetails === true ||
    (options.includeDetails !== false && result.transactions.length > 0 && result.transactions.length <= 80);

  if (includeDetails && result.transactions.length > 0) {
    drawSectionTitle(doc, labels.transactionDetail);
    if (result.transactionsTruncated) {
      doc.text(labels.truncated, MARGIN, doc.y - 2, { size: 8, color: COLORS.muted, maxWidth: CONTENT_WIDTH });
      doc.gap(12);
    }
    const widths = [90, 110, 80, 90, 80, 73];
    drawTable(
      doc,
      [labels.dateTime, labels.patient, labels.invoice, labels.method, labels.amount, labels.status],
      result.transactions.slice(0, 80).map((txn) => [
        formatZonedDateTime(txn.payment_date, locale, result.timezone),
        txn.patient_name ?? '—',
        txn.invoice_number ?? '—',
        methodLabel(txn.payment_method_key, labels),
        money(transactionNet(txn), currency, locale),
        statusLabel(txn.status, labels),
      ]),
      widths,
    );
  }

  return doc.build();
}
